import React, { useCallback } from 'react';

import { useSnackbar } from 'notistack';

import DeleteIcon from '@mui/icons-material/Delete';
import { Alert, Box, IconButton, Link } from '@mui/material';
import { DataGrid, GridColDef, GridToolbar } from '@mui/x-data-grid';

import AccommodationPropertyStateIndicator from '~/components/Accommodation/Pages/Properties/components/AccommodationPropertyStateIndicator';
import { useAccommodationServiceFetch } from '~/components/Accommodation/hooks/useAccommodationServiceFetch';

import { PropertyResponse, deleteProperty, listProperties } from '~/services/AccommodationService';

import { AccommodationPropertiesSearchInput } from './AccommodationPropertiesSearchForm';

interface Props {
  searchInput: AccommodationPropertiesSearchInput;
  pageIndex?: number;
  onPageChange: (pageIndex: number) => void;
}

const PAGE_SIZE = 20;

/** Columns hidden by default - users can enable via column selector */
const DEFAULT_HIDDEN_COLUMNS = {
  category: false,
  rating: false,
  city: false,
} as const;

/**
 * Displays a paginated grid of accommodation properties with search/filter results.
 * Supports column visibility toggling, server-side pagination, and property deletion.
 */
function AccommodationPropertiesResultsTable({ searchInput, pageIndex, onPageChange }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const getProperties = useCallback(
    () =>
      listProperties({
        name: searchInput.propertyName,
        id: searchInput.id,
        city: searchInput.city,
        country: searchInput.country,
        rating: searchInput.rating,
        propertyState: searchInput.propertyState || undefined,
        page: String((pageIndex ?? 0) + 1),
        limit: String(PAGE_SIZE),
      }),
    [pageIndex, searchInput],
  );

  const { fetchReqState, refetch } = useAccommodationServiceFetch({
    fetchFn: getProperties,
  });

  const handleDeleteProperty = async (propertyId: string) => {
    if (window.confirm('Are you sure you want to delete this property?')) {
      try {
        await deleteProperty(propertyId);
        enqueueSnackbar('Successfully deleted property', { variant: 'success' });
        refetch(); // Refresh the table after deletion
      } catch (error) {
        enqueueSnackbar('Failed to delete property', { variant: 'error' });
      }
    }
  };

  const columns: Array<GridColDef<PropertyResponse>> = [
    {
      field: 'name',
      headerName: 'Property Name',
      display: 'flex',
      minWidth: 200,
      flex: 2.5,
      renderCell: (params) => (
        <Link
          href={`/accommodation/properties/${params.row.id}`}
          target="_blank"
          className="accommodation-properties-search-result-link"
        >
          {params.value}
        </Link>
      ),
      hideable: false,
    },
    {
      field: 'chainName',
      headerName: 'Chain Name',
      minWidth: 100,
      flex: 1,
      display: 'flex',
      hideable: true,
      valueGetter: (_value, row) => row.chainName || '-',
    },
    {
      field: 'category',
      headerName: 'Category',
      minWidth: 80,
      flex: 0.8,
      display: 'flex',
      hideable: true,
    },
    {
      field: 'rating',
      headerName: 'Rating',
      width: 70,
      display: 'flex',
      hideable: true,
    },
    {
      field: 'country',
      headerName: 'Country',
      minWidth: 80,
      flex: 1,
      display: 'flex',
      hideable: true,
    },
    {
      field: 'city',
      headerName: 'City',
      minWidth: 80,
      flex: 1,
      display: 'flex',
      hideable: true,
    },
    {
      field: 'propertyState',
      headerName: 'Supplier State',
      minWidth: 150,
      flex: 1.5,
      display: 'flex',
      hideable: true,
      renderCell: (params) => <AccommodationPropertyStateIndicator property={params.row} />,
    },
    {
      field: 'actions',
      headerName: '',
      width: 60,
      sortable: false,
      renderCell: (params) => (
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            handleDeleteProperty(params.row.id);
          }}
          color="error"
          size="small"
        >
          <DeleteIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <>
      {fetchReqState.status === 'failed' && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {JSON.stringify(fetchReqState.error)}
        </Alert>
      )}
      <Box
        sx={{
          width: '100%',
          overflowX: 'auto',
          // Responsive container ensures horizontal scroll on small screens
          '& .MuiDataGrid-root': {
            minWidth: { xs: 600, sm: 800, md: '100%' },
          },
        }}
      >
        <DataGrid
          className="accommodation-properties-search-results-table"
          columns={columns}
          rows={fetchReqState.status === 'succeeded' ? fetchReqState.result.properties : []}
          rowCount={fetchReqState.status === 'succeeded' ? fetchReqState.result.total : undefined}
          getRowHeight={() => 70}
          pageSizeOptions={[PAGE_SIZE]}
          paginationMode="server"
          paginationModel={{ page: pageIndex, pageSize: PAGE_SIZE }}
          onPaginationModelChange={({ page }) => onPageChange(page)}
          loading={fetchReqState.status === 'pending'}
          autoHeight
          disableColumnFilter
          disableDensitySelector
          initialState={{
            columns: {
              columnVisibilityModel: DEFAULT_HIDDEN_COLUMNS,
            },
          }}
          slots={{
            toolbar: GridToolbar,
          }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
            },
          }}
        />
      </Box>
    </>
  );
}

export default AccommodationPropertiesResultsTable;
